import {
  PropertySearchReqTypeBody,
  PropertySearchResTypeData,
} from '../services/api';
import { SearchParamsInput, SearchParamsOutput } from '../constants/search.ts';
import { FavoriteProperties } from '../models/favorites.model.ts';
import { formatLargeNumber } from './number-formatting.ts';

export const squareFeetToSizeOptions = (selectedSize: string) => {
  if (!selectedSize) {
    return '';
  }
  const numSelectedSize = parseInt(selectedSize);

  switch (selectedSize) {
    case '1000':
    case '1,000 sqft':
      return '1,000 sqft';
    case '2000':
    case '2,000 sqft':
      return '2,000 sqft';
    case '3000':
    case '3,000 sqft':
      return '3,000 sqft';
    case '4000':
    case '4,000 sqft':
      return '4,000 sqft';
    case '5000':
    case '5,000 sqft':
      return '5,000 sqft';
    case '7500':
    case '7,500 sqft':
      return '7,500 sqft';
    case '10890':
    case '10,890 sqft':
    case '1/4 acre':
      return '1/4 acre';
    case '21780':
    case '1/2 acre':
      return '1/2 acre';
    case '43560':
    case '1 acre':
      return '1 acre';
    case '87120':
    case '2 acres':
      return '2 acres';
    case '217800':
    case '5 acres':
      return '5 acres';
    case '435600':
    case '10 acres':
      return '10 acres';
    case '871200':
    case '20 acres':
      return '20 acres';
    case '2178002':
    case '50 acres':
      return '50 acres';
    case '4356005':
    case '100 acres':
      return '100 acres';
    case 'No Min Size':
    case 'No Max Size':
      return selectedSize;
    default:
      return `${Number.isNaN(numSelectedSize) ? selectedSize : formatLargeNumber(numSelectedSize)} sqft`;
  }
};

interface FilterSearchResultsProps {
  data: PropertySearchResTypeData[] | undefined;
  params: SearchParamsOutput;
}

export const filterSearchResults = ({
  data,
  params,
}: FilterSearchResultsProps) => {
  if (!data) return data;

  if (!params.lotStatus || params.lotStatus !== 'Sold') {
    return data;
  }

  return data.filter((searchItem: PropertySearchResTypeData) => {
    return (
      searchItem &&
      searchItem?.lastSaleDate &&
      within36Months(searchItem?.lastSaleDate)
    );
  });
};

export const topLeftText = (mlsDaysOnMarket: number | undefined) => {
  const notes: string[] = [];

  if (notes.length) {
    return notes.join(', ');
  }

  return mlsDaysOnMarket ? `${mlsDaysOnMarket} days listed` : undefined;
};

// TODO: Migrate to Valibot
export const buildRequest = ({
  // Omit this property, not needed when using Valibot
  page: _page,
  ...params
}: SearchParamsOutput) => {
  // eslint-disable-next-line prefer-const
  let request: PropertySearchReqTypeBody = params || {};

  if (params.minPrice) {
    request.mlsListingPriceMin = params.minPrice;
  } else {
    delete request.mlsListingPriceMin;
  }
  if (params.maxPrice) {
    request.mlsListingPriceMax = params.maxPrice;
  } else {
    delete request.mlsListingPriceMax;
  }

  if (params.minSize) {
    request.lotSizeMin = params.minSize;
  } else {
    delete request.lotSizeMin;
  }
  if (params.maxSize) {
    request.lotSizeMax = params.maxSize;
  } else {
    delete request.lotSizeMax;
  }

  if (!params.includeNonVacantLots) {
    request.vacant = true;
  } else {
    delete request.vacant;
  }

  if (params.lotStatus === 'Sold' || params.lotStatus === 'Include Pending') {
    if (params.lotStatus === 'Sold') {
      request.mlsCancelled = true;
      delete request.mlsPending;
    } else if (params.lotStatus == 'Include Pending') {
      request.mlsPending = true;
      delete request.mlsCancelled;
    } else {
      delete request.mlsPending;
      delete request.mlsCancelled;
    }
  } else {
    delete request.mlsPending;
    delete request.mlsCancelled;
  }

  // Handled by the requesting hook itself
  if (request.size) {
    delete request.size;
  }

  // Handled by the requesting hook itself
  if (request.resultIndex) {
    delete request.resultIndex;
  }

  return request;
};

export const squareFeetToAcres = (squareFeet: number): number => {
  const squareFeetInAnAcre = 43560;
  return squareFeet / squareFeetInAnAcre;
};

const within36Months = (date: string) => {
  const today = new Date();
  const pastDate = new Date(date);

  const monthsDiff =
    (today.getFullYear() - pastDate.getFullYear()) * 12 +
    (today.getMonth() - pastDate.getMonth());

  return monthsDiff < 36;
};

export const extractIdOfSK = (inputString: string) => {
  const match = inputString.match(/#(\w+)/);
  if (match) {
    return match[1];
  }
  return null;
};

interface SortLandSearchProps {
  data: PropertySearchResTypeData[];
  queryParams: SearchParamsOutput;
  favoritesData?: FavoriteProperties[];
}

export const sortLandSearch = ({
  data,
  favoritesData,
  queryParams,
}: SortLandSearchProps) => {
  return data.sort((a, b) => {
    switch (queryParams.sort) {
      case 'Newest':
        return a.mlsDaysOnMarket - b.mlsDaysOnMarket;
      case 'Favorites':
        if (favoritesData) {
          const aIsFavorite = favoritesData.some(
            (favorite) => favorite.id === a.id
          );
          const bIsFavorite = favoritesData.some(
            (favorite) => favorite.id === b.id
          );

          if (aIsFavorite && !bIsFavorite) {
            return -1;
          } else if (!aIsFavorite && bIsFavorite) {
            return 1;
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      case 'Recently Updated':
        // if we dont have a last update date move those properties to the bottom
        // from mozilla "A negative value indicates that a should come before b"
        // so if a does not have a last update date put it after b
        if (!a.lastUpdateDate) {
          return 1;
        }
        return (
          new Date(b.lastUpdateDate).getTime() -
          new Date(a.lastUpdateDate).getTime()
        );
      case 'Price Low to high':
        return a.mlsListingPrice - b.mlsListingPrice;
      case 'Price High to low':
        return b.mlsListingPrice - a.mlsListingPrice;
      case 'Lot Size Smallest to Largest':
        return a.lotSquareFeet - b.lotSquareFeet;
      case 'Lot Size Largest to Smallest':
        return b.lotSquareFeet - a.lotSquareFeet;
      default:
        return 0;
    }
  });
};

export const buildSearchParams = (params: {
  // So long as the keys match the SearchParamsInput type and the values are easily convertible to strings, this will work.
  [K in keyof SearchParamsInput]: string | number | boolean;
}): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  return searchParams;
};
